import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  BasemapId,
  MML_ATTRIBUTION,
  MML_BASEMAPS,
  MML_LAYER_IDS,
  MML_TILE,
} from "../../utils/mmlBasemaps";
import { WGS84lalToETRSTM35FINxy } from "../../utils/coordinateConverter";
import { geometryToWsg84 } from "../../utils/road";
import {
  findNearestRoad,
  getRoadInfo,
  roadLookupError,
} from "../../services/vayvaService";
import { geocodePlace } from "../../services/maanmittauslaitosService";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || "";

/** Whole-Finland view, used when the typed municipality does not geocode. */
const FINLAND_CENTER: [number, number] = [25.7, 64.5];
const FINLAND_ZOOM = 4;

/**
 * Väylä snaps a click to the nearest road in the national register, and its
 * search radius is generous — a click can resolve to a road 50 m away. Past
 * this distance we still show the hit but warn that it may not be the one
 * the user meant.
 */
const FAR_CLICK_WARNING_M = 30;

const HIGHLIGHT_SOURCE = "picked-road";

export interface PickedRoad {
  name: string;
  municipality: string;
  lengthMeters: number | null;
}

interface SignUpRoadMapProps {
  /** What the user typed on step 2 — used only to centre the initial view. */
  municipality: string;
  picked: PickedRoad | null;
  onRoadPicked: (road: PickedRoad | null) => void;
  height?: string;
}

const BASEMAP_LABELS: { id: BasemapId; label: string }[] = [
  { id: "mml-maastokartta", label: "Maastokartta" },
  { id: "mml-taustakartta", label: "Taustakartta" },
  { id: "mml-ortokuva", label: "Ilmakuva" },
];

/**
 * Lets a registering user point at their road on a map when the name lookup
 * could not find it.
 *
 * A click anywhere is enough — we do not require the user to hit a rendered
 * road feature, because the Mapbox street tiles do not reliably carry small
 * private roads. Väylä's own snap does, and it is the same register that the
 * name lookup queries, so whatever it returns is guaranteed to be findable
 * again by name.
 */
const SignUpRoadMap: React.FC<SignUpRoadMapProps> = ({
  municipality,
  picked,
  onRoadPicked,
  height = "480px",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>("mml-taustakartta");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Guards against an earlier click's response overwriting a later one.
  const requestSeq = useRef(0);

  const drawRoad = useCallback((geometry: any) => {
    const map = mapRef.current;
    if (!map) return;

    const data: any = { type: "Feature", properties: {}, geometry };
    const source = map.getSource(HIGHLIGHT_SOURCE) as
      | mapboxgl.GeoJSONSource
      | undefined;

    if (source) {
      source.setData(data);
    } else {
      map.addSource(HIGHLIGHT_SOURCE, { type: "geojson", data });
      map.addLayer({
        id: HIGHLIGHT_SOURCE,
        type: "line",
        source: HIGHLIGHT_SOURCE,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#ffa500", "line-width": 4 },
      });
    }

    const bounds = turf.bbox(geometry);
    if (bounds.every((n) => Number.isFinite(n))) {
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 60, maxZoom: 15, duration: 800 }
      );
    }
  }, []);

  const clearRoad = useCallback(() => {
    const map = mapRef.current;
    const source = map?.getSource(HIGHLIGHT_SOURCE) as
      | mapboxgl.GeoJSONSource
      | undefined;
    source?.setData({ type: "FeatureCollection", features: [] } as any);
  }, []);

  const handleClick = useCallback(
    async (event: mapboxgl.MapMouseEvent) => {
      const seq = ++requestSeq.current;

      setLoading(true);
      setMessage(null);
      setWarning(null);

      try {
        const { lng, lat } = event.lngLat.wrap();
        const { E, N } = WGS84lalToETRSTM35FINxy({ La: lat, Lo: lng });

        const nearest = await findNearestRoad(E, N);
        if (seq !== requestSeq.current) return;

        if (!nearest) {
          onRoadPicked(null);
          clearRoad();
          setMessage(
            "Tästä kohdasta ei löytynyt tietä. Kokeile klikata lähemmäs tien viivaa."
          );
          return;
        }

        // Resolve the full geometry by the canonical name — this is the same
        // call registration will make, so a hit here proves registration works.
        const response = await getRoadInfo(nearest.name, nearest.municipality);
        if (seq !== requestSeq.current) return;

        const feature = response.data?.features?.[0];
        const geometryError = roadLookupError(response.data);
        const hasLine =
          Array.isArray(feature?.geometry?.coordinates) &&
          feature.geometry.coordinates.length > 0;

        // Some roads are in the register by name but have no usable line —
        // most often "Kadun geometria ei ole yhtenäinen", a discontiguous
        // street the interval search cannot resolve. Clicking a different
        // spot on the same road will not help, so say so rather than
        // sending the user round the same loop.
        if (geometryError || !hasLine) {
          onRoadPicked(null);
          clearRoad();
          setMessage(
            `Tietä "${nearest.name}" ei voi valita: sen reittitiedot puuttuvat ` +
              `tierekisteristä${geometryError ? ` (${geometryError})` : ""}. ` +
              `Valitse jokin toinen tie tai ota yhteyttä tukeen.`
          );
          return;
        }

        drawRoad({
          ...feature.geometry,
          coordinates: geometryToWsg84(feature),
        });

        const lengthMeters = Number(feature.properties?.viivan_pituus);
        onRoadPicked({
          name: nearest.name,
          municipality: nearest.municipality,
          lengthMeters: Number.isFinite(lengthMeters)
            ? Math.round(lengthMeters)
            : null,
        });

        if (nearest.distanceM > FAR_CLICK_WARNING_M) {
          setWarning(
            `Lähin tie oli ${Math.round(nearest.distanceM)} metrin päässä ` +
              `klikkauksestasi. Varmista, että kyseessä on oikea tie.`
          );
        }
      } catch {
        if (seq !== requestSeq.current) return;
        onRoadPicked(null);
        setMessage("Tien haku epäonnistui. Tarkista verkkoyhteytesi.");
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [clearRoad, drawRoad, onRoadPicked]
  );

  // Keep the click handler fresh without tearing the map down on every render.
  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  // Create the map once per mount.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/outdoors-v11",
      center: FINLAND_CENTER,
      zoom: FINLAND_ZOOM,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.on("load", () => {
      for (const layer of MML_LAYER_IDS) {
        map.addSource(layer, {
          type: "raster",
          tiles: [MML_TILE(MML_BASEMAPS[layer])],
          tileSize: 256,
          attribution: MML_ATTRIBUTION,
        });
        map.addLayer({
          id: layer,
          type: "raster",
          source: layer,
          layout: { visibility: "none" },
        });
      }
      setMapReady(true);
    });

    map.on("click", (event) => handleClickRef.current(event));
    map.on("mouseenter", () => {
      map.getCanvas().style.cursor = "crosshair";
    });

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Basemap layers are added hidden, so switching only flips visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;

    for (const id of MML_LAYER_IDS) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(
          id,
          "visibility",
          id === basemap ? "visible" : "none"
        );
      }
    }
  }, [mapReady, basemap]);

  // Centre on the municipality the user typed. It is usually right even when
  // the road name is not, and it saves them panning across the country.
  useEffect(() => {
    if (!mapReady || !municipality.trim()) return;

    let cancelled = false;
    geocodePlace(municipality).then((center) => {
      if (cancelled || !center || !mapRef.current) return;
      mapRef.current.jumpTo({ center, zoom: 11 });
    });

    return () => {
      cancelled = true;
    };
  }, [mapReady, municipality]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Klikkaa karttaa tiesi kohdalta. Haemme tien nimen automaattisesti
        kansallisesta tierekisteristä.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={basemap}
        onChange={(_, value) => value && setBasemap(value as BasemapId)}
        aria-label="Karttataso"
      >
        {BASEMAP_LABELS.map(({ id, label }) => (
          <ToggleButton key={id} value={id}>
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ position: "relative", height, minHeight: 260 }}>
        <div
          ref={containerRef}
          className="map-container"
          style={{ position: "absolute", inset: 0 }}
        />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxShadow: 2,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption">Haetaan tietä…</Typography>
          </Box>
        )}
      </Box>

      {message && <Alert severity="warning">{message}</Alert>}

      {picked && (
        <Alert
          severity="success"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                onRoadPicked(null);
                clearRoad();
                setWarning(null);
              }}
            >
              Vaihda
            </Button>
          }
        >
          <strong>
            {picked.name}, {picked.municipality}
          </strong>
          {picked.lengthMeters !== null && ` · ${picked.lengthMeters} m`}
        </Alert>
      )}

      {warning && <Alert severity="info">{warning}</Alert>}
    </Stack>
  );
};

export default SignUpRoadMap;
